import { Pencil, Trash2 } from 'lucide-react'
import { useState } from 'react'
import { useNavigate } from 'react-router'
import { Async, type AsyncState } from '../../platform/async/Async'
import { Skeleton } from '../../platform/async/Skeleton'
import { emit } from '../../platform/analytics/analytics'
import { useScreenViewed } from '../../platform/analytics/useScreenViewed'
import { Button } from '../../platform/primitives/Button'
import { DataGrid, type Column } from '../../platform/primitives/DataGrid'
import { InlineLink } from '../../platform/primitives/InlineLink'
import { Panel } from '../../platform/primitives/Panel'
import { SearchField } from '../../platform/primitives/SearchField'
import { usePageMeta } from '../../shell/PageMeta'
import { useTagCount, useTagList } from '../transactions/useTransactions'
import { Icon } from '../../platform/icons/Icon'
import { useHitTarget } from '../../platform/responsive/hitTarget'
import { CreateTagDialog } from './CreateTagDialog'
import { DeleteTagDialog } from './DeleteTagDialog'
import { RenameTagDialog } from './RenameTagDialog'
import { tagTransactionsHref } from './tagPaths'
import { canAddress } from './useTagMutations'
import './tags.css'

const PAGE_SIZE = 50

/** D8: a case-insensitive substring, as iOS `localizedCaseInsensitiveContains`. */
export function filterTags(tags: readonly string[], query: string): string[] {
  const q = query.trim().toLowerCase()
  return q === '' ? [...tags] : tags.filter((t) => t.toLowerCase().includes(q))
}

function TagCountCell({ name }: { name: string }) {
  const result = useTagCount(name, true)
  if (result.status === 'loading') return <Skeleton width="2ch" height={14} />
  if (result.status === 'error' || result.count === undefined) {
    return (
      <span className="tags__count">
        —<span className="sr-only">count unavailable</span>
      </span>
    )
  }
  return <span className="tags__count">{result.count.toLocaleString()}</span>
}

const fireViewed = (all: readonly string[]) => emit('tags_viewed', { tags_count: all.length })

export function Tags() {
  const navigate = useNavigate()
  const [today] = useState(() => new Date())
  const [query, setQuery] = useState('')
  const [page, setPage] = useState(1)
  const state = useTagList()
  const target = useHitTarget('page')
  const [renaming, setRenaming] = useState<string | null>(null)
  const [deleting, setDeleting] = useState<string | null>(null)
  const [creating, setCreating] = useState(false)

  useScreenViewed(state, fireViewed)
  const total = state.status === 'success' ? state.data.length : state.status === 'empty' ? 0 : undefined
  usePageMeta(total === undefined ? null : total === 1 ? '1 tag' : `${total} tags`)

  const columns: Column<string>[] = [
    {
      key: 'tag',
      header: 'Tag',
      width: 'minmax(0, 1fr)',
      render: (t) => (
        <InlineLink to={tagTransactionsHref(t, today)} aria-label={`Show transactions tagged ${t}`} className="tags__name">
          {`#${t}`}
        </InlineLink>
      ),
    },
    {
      key: 'count',
      // "Transactions" is 94px of caps at the header size and ran 18px under "Manage" in a 64px track. The
      // short label is what the eye reads; the full one is the column's accessible name
      // (an sr-only sibling would sit inside the header's box and still be measured as overflow).
      header: (
        <span role="img" aria-label="Transactions">
          Uses
        </span>
      ),
      width: '48px',
      align: 'end',
      render: (t) => <TagCountCell name={t} />,
    },
    {
      key: 'manage',
      header: 'Manage',
      // Two targets plus the capsule's 2px gap, 2px padding either side and 1px border either side.
      width: `${2 * target + 8}px`,
      align: 'end',
      // `.` and `..` cannot be addressed by any URL, so a control would be a guaranteed 405.
      render: (t) =>
        canAddress(t) ? (
          <span className="tags__manage-cell">
            <button
              type="button"
              className="tags__manage-btn hoverable"
              style={{ minHeight: target, minWidth: target }}
              aria-label={`Rename ${t}`}
              onClick={() => setRenaming(t)}
            >
              <Icon glyph={Pencil} size={14} />
            </button>
            <button
              type="button"
              className="tags__manage-btn hoverable"
              style={{ minHeight: target, minWidth: target }}
              aria-label={`Delete ${t}`}
              onClick={() => setDeleting(t)}
            >
              <Icon glyph={Trash2} size={14} />
            </button>
          </span>
        ) : (
          <span className="tags__manage-empty">—</span>
        ),
    },
  ]

  const all = state.status === 'success' ? state.data : []
  const filtered = filterTags(all, query)
  const filtering = query.trim() !== ''
  const lastPage = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE))
  const current = Math.min(page, lastPage)
  const from = (current - 1) * PAGE_SIZE
  const visible = filtered.slice(from, from + PAGE_SIZE)

  // A narrowing that matches nothing is not "you have no tags" — the second says the list is empty,
  // the first says the filter is.
  const shown: AsyncState<string[]> =
    state.status === 'success' && filtered.length === 0 ? { status: 'empty' } : state

  const clear = () => {
    setQuery('')
    setPage(1)
  }
  const empty =
    state.status === 'success' && filtering
      ? { title: `No tag matches “${query.trim()}”`, action: { label: 'Clear filter', onAct: clear } }
      : {
          title: 'No tags yet',
          body: 'Tags you add to transactions show up here.',
          action: { label: 'Go to transactions', onAct: () => void navigate('/transactions') },
        }

  return (
    <>
      <div className="tags-toolbar">
        <SearchField
          className="tags-toolbar__search"
          label="Filter tags"
          placeholder="Filter tags"
          value={query}
          onChange={(v) => {
            setQuery(v)
            setPage(1)
          }}
        />
        <Button variant="primary" size="page" className="tags-toolbar__new" onClick={() => setCreating(true)}>
          New tag
        </Button>
      </div>
      <Panel>
        <Async
          surface="tags"
          state={shown}
          skeleton={
            <>
              {Array.from({ length: 8 }, (_, i) => (
                <Skeleton key={i} height={44} />
              ))}
            </>
          }
          empty={empty}
        >
          {() => (
            <>
              <DataGrid
                columns={columns}
                groups={[{ label: '', rows: visible }]}
                rowKey={(t) => t}
                label="Tags"
                empty="No tags on this page."
              />
              {filtered.length > PAGE_SIZE && (
                <div className="tags__pager">
                  <Button variant="outline" size="panel" disabled={current <= 1} onClick={() => setPage(current - 1)}>
                    Prev
                  </Button>
                  <span className="tags__pager-label">{`${from + 1}–${from + visible.length} of ${filtered.length}`}</span>
                  <Button variant="outline" size="panel" disabled={current >= lastPage} onClick={() => setPage(current + 1)}>
                    Next
                  </Button>
                </div>
              )}
            </>
          )}
        </Async>
      </Panel>
      {creating && <CreateTagDialog tags={all} onClose={() => setCreating(false)} />}
      {renaming !== null && <RenameTagDialog name={renaming} tags={all} onClose={() => setRenaming(null)} />}
      {deleting !== null && <DeleteTagDialog name={deleting} onClose={() => setDeleting(null)} />}
    </>
  )
}
